// Data-driven registry of safety signs (ISO 7010 / ASR A1.3) used by the
// Betriebsanweisung builder and PDF. Each entry maps a stable `key` to an SVG
// file under `public/safetySymbols/<category>/<key>.svg`, plus i18n label keys.
//
// The artwork is intentionally decoupled: drop the official / open-licensed
// SVGs in by these filenames and they appear everywhere automatically. A sign
// whose file is missing renders a labelled colored-frame fallback in the picker.
//
// Categories follow the four ISO 7010 sign types and their colors:
//   mandatory   -> blue circle   (Gebotszeichen)
//   prohibition -> red circle    (Verbotszeichen)
//   warning     -> yellow triangle (Warnzeichen)
//   rescue      -> green square   (Rettungszeichen)

export const SYMBOL_CATEGORIES = [
  { key: "ghs", color: "#c8102e", labelKey: "safetySymbols.categories.ghs" },
  { key: "mandatory", color: "#005387", labelKey: "safetySymbols.categories.mandatory" },
  { key: "prohibition", color: "#c8102e", labelKey: "safetySymbols.categories.prohibition" },
  { key: "warning", color: "#f9a800", labelKey: "safetySymbols.categories.warning" },
  { key: "rescue", color: "#009639", labelKey: "safetySymbols.categories.rescue" },
  { key: "fire", color: "#c8102e", labelKey: "safetySymbols.categories.fire" },
];

// GHS pictograms reuse the artwork already shipped under /dangerousGoods.
const GHS_FILE = {
  "ghs-corrosive": "/dangerousGoods/pictogram_corrosive_kat_1.png",
  "ghs-environment": "/dangerousGoods/pictogram_environmental_risk.png",
  "ghs-explosive": "/dangerousGoods/pictogram_explosive.png",
  "ghs-flammable": "/dangerousGoods/pictogram_flammable.png",
  "ghs-gas": "/dangerousGoods/pictogram_gas.png",
  "ghs-health": "/dangerousGoods/pictogram_health_risk.png",
  "ghs-oxidizing": "/dangerousGoods/pictogram_oxidizing.png",
  "ghs-toxic": "/dangerousGoods/pictogram_poisonous_kat_1_3.png",
  "ghs-harmful": "/dangerousGoods/pictogram_poisonous_kat_4.png",
};

// labelKey resolves to safetySymbols.signs.<key>
const flat = (basename) => `/safetySymbols/${encodeURI(basename)}`;

const sign = (category, key, iso, fileOverride) => ({
  key,
  category,
  iso, // reference ISO 7010 code, for the manifest
  file:
    category === "ghs"
      ? GHS_FILE[key]
      : fileOverride
        ? flat(fileOverride)
        : iso
          ? flat(`ISO 7010 ${iso}.SVG`)
          : `/safetySymbols/${category}/${key}.svg`,
  labelKey: `safetySymbols.signs.${key}`,
});

export const SAFETY_SYMBOLS = [
  // GHS / CLP hazard pictograms (real artwork shipped).
  sign("ghs", "ghs-corrosive"),
  sign("ghs", "ghs-environment"),
  sign("ghs", "ghs-explosive"),
  sign("ghs", "ghs-flammable"),
  sign("ghs", "ghs-gas"),
  sign("ghs", "ghs-health"),
  sign("ghs", "ghs-oxidizing"),
  sign("ghs", "ghs-toxic"),
  sign("ghs", "ghs-harmful"),

  // Mandatory (Gebotszeichen) - blue circle
  sign("mandatory", "eye-protection", "M004"),
  sign("mandatory", "ear-protection", "M003"),
  sign("mandatory", "head-protection", "M014"),
  sign("mandatory", "foot-protection", "M008"),
  sign("mandatory", "hand-protection", "M009"),
  sign("mandatory", "high-visibility", "M015"),
  sign("mandatory", "respiratory-protection", "M016"),
  sign("mandatory", "face-protection", "M013"),
  sign("mandatory", "protective-clothing", "M010"),
  sign("mandatory", "read-instructions", "M002"),

  // Prohibition (Verbotszeichen) - red circle + bar
  sign("prohibition", "no-smoking", "P002"),
  sign("prohibition", "no-open-flame", "P003"),
  sign("prohibition", "no-access", "P006"),
  sign("prohibition", "no-eating-drinking", "P022"),
  sign("prohibition", "do-not-touch", "P010"),
  sign("prohibition", "no-gloves", "P028"),
  sign("prohibition", "no-unauthorized-access", null, "DIN 4844 D-P006.SVG"),

  // Warning (Warnzeichen) - yellow triangle
  sign("warning", "general", "W001"),
  sign("warning", "explosive-atmosphere", null, "DIN 4844 D-W021.SVG"),
  sign("warning", "electricity", "W012"),
  sign("warning", "flammable", "W021"),
  sign("warning", "hot-surface", "W017"),
  sign("warning", "laser", "W004"),
  sign("warning", "biohazard", "W009"),
  sign("warning", "toxic", "W016"),
  sign("warning", "slippery", "W011"),
  sign("warning", "automatic-start", "W018"),
  sign("warning", "hand-crushing", "W024"),

  // Rescue / first aid (Rettungszeichen) - green square
  sign("rescue", "first-aid", "E003"),
  sign("rescue", "emergency-exit-left", "E001"),
  sign("rescue", "emergency-exit-right", "E002"),
  sign("rescue", "emergency-phone", "E004"),
  sign("rescue", "eyewash", "E011"),
  sign("rescue", "safety-shower", "E012"),
  sign("rescue", "defibrillator", "E010"),
  sign("rescue", "assembly-point", "E007"),

  sign("fire", "fire-extinguisher", "F001"),
  sign("fire", "fire-hose-reel", "F002"),
  sign("fire", "fire-ladder", "F003"),
  sign("fire", "firefighting-equipment", "F004"),
  sign("fire", "fire-alarm-call-point", "F005"),
  sign("fire", "fire-emergency-telephone", "F006"),
];

export function symbolsByCategory(category) {
  return SAFETY_SYMBOLS.filter((s) => s.category === category);
}

export function symbolByKey(key) {
  return SAFETY_SYMBOLS.find((s) => s.key === key) || null;
}

export function categoryColor(category) {
  const c = SYMBOL_CATEGORIES.find((x) => x.key === category);
  return c ? c.color : "#9e9e9e";
}
